<template>
  <div class="add-delegate-modal">
    <!-- Step 1: Search Email -->
    <div v-if="step === 'search'">
      <div class="form-group mb-3">
        <label class="form-control-label">Email</label>
        <base-input
          v-model="email"
          placeholder="Email Address"
          name="email"
          type="email"
          @keyup.enter.native="checkEmail"
        ></base-input>
      </div>

      <div class="text-right mt-4">
        <el-button @click="$emit('close')">Cancel</el-button>
        <el-button type="primary" :loading="loading" @click="checkEmail" :disabled="!email">Next</el-button>
      </div>
    </div>

    <!-- Step 2: Confirm Found Lead -->
    <div v-if="step === 'confirm'">
      <div class="alert alert-success">
        <strong>User found!</strong>
      </div>
      <div class="d-flex align-items-center mb-4">
        <div class="avatar rounded-circle mr-3">
            <img src="/img/custom/user.png" />
        </div>
        <div>
          <h4 class="mb-0">{{ foundLead.first_name }} {{ foundLead.last_name }}</h4>
          <small>{{ foundLead.email }}</small>
        </div>
      </div>

      <div class="text-right mt-4">
        <el-button @click="reset">Back</el-button>
        <el-button type="primary" :loading="loading" @click="confirmAddDelegate">Add User</el-button>
      </div>
    </div>

    <!-- Step 3: Create New Lead -->
    <div v-if="step === 'create'">
      <div class="alert alert-info">
        User not found. Please create a new lead.
      </div>
      
      <div class="row">
        <div class="col-6">
          <base-input label="First Name" v-model="newLead.first_name" placeholder="First Name"></base-input>
        </div>
        <div class="col-6">
          <base-input label="Last Name" v-model="newLead.last_name" placeholder="Last Name"></base-input>
        </div>
      </div>

      <base-input label="Email Address" v-model="newLead.email" disabled></base-input>
      
      <div class="row">
        <div class="col-6">
          <base-input label="Phone Number" v-model="newLead.phone" placeholder="0400 123 123"></base-input>
        </div>
        <div class="col-6">
           <div class="form-group">
            <label class="form-control-label">Country</label>
            <el-select v-model="newLead.country" placeholder="Select" class="w-100">
                <el-option label="AU" value="AU"></el-option>
                <el-option label="UK" value="UK"></el-option>
            </el-select>
           </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Company</label>
        <el-select
          v-model="newLead.company_id"
          filterable
          remote
          reserve-keyword
          placeholder="Search or select a company..."
          :remote-method="searchCompanies"
          :loading="searchingCompanies"
          class="w-100"
        >
          <el-option
            v-for="company in companySearchResults"
            :key="company.id"
            :label="company.company_name"
            :value="company.id"
          >
          </el-option>
        </el-select>
      </div>

      <div class="text-right mt-4">
        <el-button @click="reset">Back</el-button>
        <el-button type="primary" :loading="loading" @click="createAndAddLead">Save</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import LeadService from "@/services/lead.service";
import LeadEventService from "@/services/lead-event.service";
import CompanyService from '@/services/company.service';
import { Button, Select, Option } from "element-ui";

export default {
  name: "AddDelegateModal",
  components: {
    [Button.name]: Button,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      step: "search", // search, confirm, create
      email: "",
      loading: false,
      foundLead: null,
      newLead: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        country: "AU",
        company_id: null,
      },
      // Company search
      companySearchResults: [],
      searchingCompanies: false,
      searchTimeout: null
    };
  },
  methods: {
    reset() {
        this.step = 'search';
        this.foundLead = null;
    },
    async checkEmail() {
      if (!this.email) return;
      
      this.loading = true;
      try {
        const response = await LeadService.searchLeads({ query: this.email }); // Assuming searchLeads takes q param
        // The search API usually returns a list.
        const leads = response.data.data || response.data; // handling potential pagination structure
        const match = leads.find(l => l.email.toLowerCase() === this.email.toLowerCase());
        
        if (match) {
          this.foundLead = match;
          this.step = "confirm";
        } else {
          this.newLead.email = this.email;
          this.step = "create";
        }
      } catch (error) {
        console.error("Error searching lead:", error);
         // Fallback to create if search fails or whatever logic handles 'not found' best
         // But usually 404 or empty list. Assuming empty list for search endpoint.
         this.newLead.email = this.email;
         this.step = "create";
      } finally {
        this.loading = false;
      }
    },
    async confirmAddDelegate() {
      await this.delegateToLead(this.foundLead.id);
    },
    async createAndAddLead() {
        if (!this.newLead.first_name || !this.newLead.last_name) {
            this.$store.dispatch("alert/error", "Please enter first and last name.", { root: true });
            return;
        }

        this.loading = true;
        try {
            const response = await LeadService.createAssociatedLead(this.leadEvent.lead_id, this.newLead);
            const createdLead = response;
            if (this.newLead.company_id) {
                 await this.$store.dispatch('companies/associateLeads', {
                    company_id: this.newLead.company_id,
                    lead_ids: [createdLead.id]
                });
            }
            await this.delegateToLead(createdLead.id);
        } catch (error) {
             console.error("Error creating lead:", error);
             this.$store.dispatch("alert/error", "Failed to create lead.", { root: true });
             this.loading = false;
        }
    },
    async delegateToLead(leadId) {
        this.loading = true;
        try {
            await LeadEventService.addDelegate(this.leadEvent.id, leadId);
            this.$emit("added");
            this.$store.dispatch("alert/success", "Delegate added successfully.", { root: true });
        } catch (error) {
             console.error("Error adding delegate:", error);
             this.$store.dispatch("alert/error", "Failed to add delegate.", { root: true });
        } finally {
            this.loading = false;
        }
    },
    async searchCompanies(query) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (!query || query.length <= 2) {
        this.companySearchResults = [];
        return;
      }
      this.searchingCompanies = true;
      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await CompanyService.searchCompanies({ query: query });
          this.companySearchResults = response.data || [];
        } catch (error) {
          console.error('Failed to search companies:', error);
        } finally {
          this.searchingCompanies = false;
        }
      }, 500);
    },
  },
};
</script>

<style scoped>
.add-delegate-modal {
    padding: 10px;
}
.rounded-circle img {
    width: 48px;
    height: 48px;
}
</style>
